import React, { type ReactNode, useMemo } from "react";
import { Platform, StyleSheet } from "react-native";

import { LinearGradient } from "../../shared/components/LinearGradient/linear-gradient";
import { Button } from "../Button";
import { Flex } from "../Flex";
import { Heading } from "../Heading";
import { CheckCircle, Close, Error, Information, Warning } from "../icons";
import type { LiveRegionType } from "../LiveRegion";
import { Text } from "../Text";
import { useTheme } from "../ThemeProvider";
import type { StrictSpacingStyle } from "../util/types";
import { type AlertAction, type AlertActionLayout, AlertActions } from "./AlertActions";

interface AlertProps {
  /**
   * Defines a custom accessibility label for screen readers when using inline layout.
   * Overrides the default combination of title and explainer text.
   * Useful for providing localized accessibility text or custom formatting.
   *
   * Note: Only applies to inline layout where text grouping is needed for proper
   * reading order. For stacked layout, the default behavior is sufficient.
   */
  accessibilityLabel?: string;

  /**
   * Defines the label and callback for each action button.
   */
  actions?: AlertAction[];

  /**
   * Defines the dismiss button's aria-label and callback.
   */
  dismissAction?: {
    ariaLabel: string;
    onDismiss: () => void;
    /** Draws a visible native focus ring for hardware-keyboard users. */
    showNativeFocusRing?: boolean;
  };

  /**
   * Defines the Alert's content.
   */
  explainerText?: string | ReactNode;

  /**
   * Defines a custom icon to override the default status icon.
   */
  icon?: React.ReactElement;

  /**
   * Defines the layout of the Alert's action button(s). Defaults to `inline`.
   */
  layout?: AlertActionLayout;

  /**
   * Defines the status color. Defaults to `info`.
   */
  status?: "critical" | "info" | "success" | "warning" | "ai";

  /**
   * Defines the padding/margin style overrides to apply to the Alert's container.
   */
  sx?: StrictSpacingStyle;

  /**
   * Defines the Alert's title content.
   */
  title?: string;

  /**
   * Defines the screen reader announcement type.
   * More details here: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Attributes/aria-live
   */
  type?: LiveRegionType;

  /**
   * Should show the icon, default is true
   */
  showIcon?: boolean;
}

const defaultIcons = {
  critical: <Error />,
  info: <Information />,
  success: <CheckCircle />,
  warning: <Warning />,
  ai: <Information />,
};

type StatusVariant = {
  backgroundColor?: string | readonly [string, string, ...string[]];
  color: string | string[];
};

export function Alert({
  accessibilityLabel: customAccessibilityLabel,
  actions,
  dismissAction,
  explainerText,
  icon,
  layout = "inline",
  status = "info",
  sx,
  title,
  type = "polite",
  showIcon = true,
}: AlertProps) {
  const theme = useTheme();
  const statusVariants: Record<NonNullable<AlertProps["status"]>, StatusVariant> = {
    critical: {
      backgroundColor: theme.colors.backgroundStatusCriticalWeaker,
      color: theme.colors.foregroundStatusCritical,
    },
    info: {
      backgroundColor: theme.colors.backgroundStatusInfoWeaker,
      color: theme.colors.foregroundStatusInfo,
    },
    success: {
      backgroundColor: theme.colors.backgroundStatusSuccessWeaker,
      color: theme.colors.foregroundStatusSuccess,
    },
    warning: {
      backgroundColor: theme.colors.backgroundStatusWarningWeaker,
      color: theme.colors.foregroundStatusWarning,
    },
    ai: {
      backgroundColor: [theme.colors.utilityAiWeakStart, theme.colors.utilityAiWeakEnd],
      color: [theme.colors.utilityAiStrongStart, theme.colors.utilityAiStrongEnd],
    },
  };

  const styles = useMemo(
    () =>
      StyleSheet.create({
        background: {
          borderRadius: theme.borderRadius.lg,
          alignSelf: "stretch",
        },
        container: {
          flexDirection: "row",
          gap: theme.spacing.s3,
          padding: theme.spacing.s4,
          borderRadius: theme.borderRadius.lg,
        },
        content: {
          flex: 1,
          justifyContent: "center",
        },
        title: {
          marginBottom: 0,
        },
        dismissActionContainer: {
          alignSelf: "flex-start",
        },
        dismissAction: {
          // Counteract margin added by button. This allows the button's icon to align with other icons in the alert.
          margin: -theme.spacing.sc3,
        },
      }),
    [theme],
  );

  const currStatus = statusVariants[status];
  const styledIcon = useMemo(() => {
    if (!showIcon) {
      return <></>;
    }

    const baseIcon = icon || defaultIcons[status];
    return React.cloneElement(baseIcon, {
      "aria-hidden": true,
      color: currStatus.color,
      colors: currStatus.color,
      testID: "status-icon",
    });
  }, [icon, status, currStatus.color, showIcon]);

  const shouldUseGradient =
    Array.isArray(currStatus.backgroundColor) && currStatus.backgroundColor.length >= 2;

  // For inline layout, group title and explainer text together for the screen reader.
  // This ensures proper reading order: title + description, then action buttons, then close button.
  const hasExplainer =
    typeof explainerText === "string"
      ? Boolean(explainerText.trim())
      : explainerText != null && explainerText;

  // Per-platform grouping policy. `accessible={true}` enables screen-reader text
  // grouping (one swipe reads title + explainer together), but it also makes the
  // element a hardware-keyboard focus stop. For non-interactive text content that
  // focus stop is a dead-end (Tab lands on it, Enter does nothing), so we only opt
  // in where grouping earns its keep:
  //   - android: never. accessible={true} implicitly calls setFocusable(true),
  //              trapping Tab focus in non-interactive Alerts.
  //   - ios:     only when title + explainer are both present, so grouping turns
  //              two VoiceOver swipes into one. Single-text alerts gain nothing
  //              from grouping and would just create a Full Keyboard Access (FKA)
  //              dead-end via accessible={true}.
  //   - default (web): whenever there's any text — no focus side-effects to dodge.
  const shouldGroupTextForAccessibility =
    layout === "inline" &&
    Platform.select({
      android: false,
      ios: !!title && !!hasExplainer,
      default: !!(title || hasExplainer),
    });

  // Build accessibility label: custom > combined > individual > undefined
  const getAccessibilityLabel = (): string | undefined => {
    if (customAccessibilityLabel) return customAccessibilityLabel;
    if (typeof explainerText === "string") {
      if (!title && !explainerText.trim()) return undefined;
      if (title && explainerText.trim()) return `${title} ${explainerText}`;
      return title || explainerText.trim() || undefined;
    }
    return title?.trim() || undefined;
  };

  const accessibilityLabel = getAccessibilityLabel();

  const alertContent = (
    <Flex
      id="alert"
      role="alert"
      aria-live={type}
      sx={{
        ...styles.container,
        ...(typeof currStatus.backgroundColor === "string"
          ? { backgroundColor: currStatus.backgroundColor }
          : {}),
        ...sx,
      }}
    >
      {styledIcon}
      <Flex
        id="alert-content"
        sx={styles.content}
        accessible={shouldGroupTextForAccessibility}
        accessibilityLabel={shouldGroupTextForAccessibility ? accessibilityLabel : undefined}
      >
        {title && title.trim() && (
          <Heading level={3} sx={styles.title}>
            {title}
          </Heading>
        )}
        {typeof explainerText === "string"
          ? explainerText.trim() && (
              <Text level={2} variant="contrast">
                {explainerText}
              </Text>
            )
          : explainerText != null && explainerText}
        {actions && layout === "stacked" && <AlertActions actions={actions} layout={layout} />}
      </Flex>
      {actions && layout === "inline" && <AlertActions actions={actions} layout={layout} />}
      {dismissAction && (
        <Flex sx={styles.dismissActionContainer}>
          <Button
            ariaLabel={dismissAction.ariaLabel}
            onPress={dismissAction.onDismiss}
            showNativeFocusRing={dismissAction.showNativeFocusRing}
            sx={styles.dismissAction}
            variant="tertiary"
            iconBefore={<Close aria-hidden size="md" color={theme.colors.foregroundActionStrong} />}
            iconOnly={true}
          />
        </Flex>
      )}
    </Flex>
  );

  if (shouldUseGradient && Array.isArray(currStatus.backgroundColor)) {
    return (
      <LinearGradient
        style={styles.background}
        colors={
          typeof currStatus.backgroundColor === "string"
            ? [currStatus.backgroundColor, currStatus.backgroundColor]
            : currStatus.backgroundColor
        }
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
      >
        {alertContent}
      </LinearGradient>
    );
  } else {
    return alertContent;
  }
}
